import { useState, useEffect, useMemo } from 'react';
import { kioskApi } from '../lib/kiosk-api.js';
import { bigVariance, countedLines, newToken, readDraft, writeDraft, clearDraft } from '../lib/stock-count.js';

// Stock Phase 2 — the count sheet on the store kiosk (docs/STOCK-SPEC.md §7.3;
// CJ 2026-10-17). Every tracked item for this store on one scrolling list,
// next to Square's count. Type what's on the shelf as you walk, review, and one
// Send sets those counts in Square. Kiosk only — not in the app or website.

const errText = (e) => { try { return JSON.parse(e.message).error || e.message; } catch { return e.message; } };
const btn = {
  padding: '10px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff',
  fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const primary = { ...btn, background: 'var(--forest)', color: '#fff', borderColor: 'var(--forest)' };
const label = (i) => (i.variation_name && i.variation_name !== 'Regular' ? `${i.item_name} — ${i.variation_name}` : i.item_name);
const store = typeof window !== 'undefined' ? window.localStorage : null;

export function KioskCountView({ session }) {
  const key = session.location.key;
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(() => (store && readDraft(store, key)) || { token: newToken(), values: {} });
  const [reviewing, setReviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    kioskApi('GET', `/api/kiosk/${key}/stock/sheet`, null, session.token)
      .then((r) => setItems(r.items))
      .catch((e) => setError(errText(e)));
  }, [key, session.token]);

  useEffect(() => { if (store) writeDraft(store, key, draft); }, [draft, key]);

  const setValue = (id, raw) => setDraft((d) => ({ ...d, values: { ...d.values, [id]: raw.replace(/\D/g, '').slice(0, 6) } }));
  const lines = useMemo(() => (items ? countedLines(items, draft.values) : []), [items, draft.values]);
  const flagged = lines.filter((l) => bigVariance(l.on_hand, l.counted));

  async function send() {
    setSending(true);
    setError('');
    try {
      const r = await kioskApi('POST', `/api/kiosk/${key}/stock/count`, {
        client_token: draft.token,
        lines: lines.map((l) => ({ stock_item_id: l.stock_item_id, quantity: l.counted })),
      }, session.token);
      if (store) clearDraft(store, key);
      setDraft({ token: newToken(), values: {} });
      setReviewing(false);
      setDone(r.sent);
      const fresh = await kioskApi('GET', `/api/kiosk/${key}/stock/sheet`, null, session.token);
      setItems(fresh.items);
    } catch (e) {
      setError(errText(e));
    } finally {
      setSending(false);
    }
  }

  if (error && !items) return <div style={{ padding: 16, color: '#c62828' }}>{error}</div>;
  if (!items) return <div style={{ padding: 16, color: '#757575' }}>Loading the count sheet…</div>;
  if (!items.length) return <div style={{ padding: 16, color: '#757575' }}>Nothing is tracked in Square at this store.</div>;

  if (reviewing) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', display: 'grid', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Check before sending</h2>
        <div style={{ fontSize: 14, color: '#616161' }}>
          {lines.length} item{lines.length === 1 ? '' : 's'} will be set in Square. Items you left blank are not changed.
        </div>
        {flagged.length > 0 && (
          <div style={{ background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 10, padding: 12, fontSize: 14 }}>
            <strong>{flagged.length} count{flagged.length === 1 ? ' is' : 's are'} far from Square's number.</strong> Check {flagged.length === 1 ? 'it' : 'them'} before sending.
          </div>
        )}
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10 }}>
          {lines.map((l) => {
            const big = bigVariance(l.on_hand, l.counted);
            return (
              <div key={l.stock_item_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 14px',
                borderBottom: '1px solid #f2f2f2', background: big ? '#fff8e1' : 'transparent' }}>
                <span style={{ minWidth: 0 }}>{label(l)}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: big ? 700 : 400 }}>
                  {l.on_hand} → {l.counted}
                </span>
              </div>
            );
          })}
        </div>
        {error && <div style={{ color: '#c62828', fontSize: 14 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btn} disabled={sending} onClick={() => setReviewing(false)}>Back to the sheet</button>
          <button style={primary} disabled={sending} onClick={send}>{sending ? 'Sending…' : `Send ${lines.length} to Square`}</button>
        </div>
      </div>
    );
  }

  const q = filter.trim().toLowerCase();
  const shown = q ? items.filter((i) => label(i).toLowerCase().includes(q)) : items;
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Inventory count</h2>
        <button style={lines.length ? primary : { ...btn, color: '#9e9e9e' }} disabled={!lines.length} onClick={() => { setDone(null); setReviewing(true); }}>
          Review {lines.length || ''} count{lines.length === 1 ? '' : 's'}
        </button>
      </div>
      {done !== null && (
        <div style={{ background: '#e8f5e9', border: '1px solid #81c784', borderRadius: 10, padding: 12, fontSize: 14 }}>
          Sent {done} count{done === 1 ? '' : 's'} to Square.
        </div>
      )}
      <div style={{ fontSize: 13, color: '#616161' }}>
        Type what's on the shelf. Leave an item blank to skip it. Your numbers are saved as you go.
      </div>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find an item"
        style={{ padding: 10, fontSize: 15, borderRadius: 8, border: '1px solid #ccc', fontFamily: 'inherit' }} />
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10 }}>
        {shown.map((i) => {
          const raw = draft.values[i.stock_item_id] ?? '';
          const big = raw !== '' && bigVariance(i.on_hand, Number(raw));
          return (
            <div key={i.stock_item_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', borderBottom: '1px solid #f2f2f2' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{label(i)}</div>
                <div style={{ fontSize: 12, color: i.on_hand < 0 ? '#c62828' : '#9e9e9e' }}>Square says {i.on_hand}</div>
              </div>
              <input inputMode="numeric" value={raw} onChange={(e) => setValue(i.stock_item_id, e.target.value)}
                aria-label={`How many ${label(i)} are on the shelf`}
                style={{ width: 90, padding: 10, fontSize: 18, textAlign: 'right', borderRadius: 8, fontFamily: 'inherit',
                          border: `2px solid ${big ? '#ef6c00' : '#ccc'}` }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
